import SVG from "react-inlinesvg";
import { useSelector } from "react-redux";
import styles from "./Sidebar.module.scss";

import { omuniSVG } from "../../../shared/helpers/icons";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { companiesSelector } from "../../../companies/selectors/companies";
import { ICONS } from "../../helpers/icons";
import { Item } from "../Item/Item";
import { SubMenuItem } from "../SubMenuItem/SubMenuItem";

import { useTranslation } from "react-i18next";
import { environment, omuniWeb } from "../../../app/conf";
import { permissionsSelector } from "../../../auth/selectors/permissions";
import { COMPANIES } from "../../../shared/constants/companies";
import { ItemMigration } from "../ItemMigration/ItemMigration";
import Version from "../Version/Version";
import { ENVIRONMENTS, MODULES, STATUS } from "./types";
import { usePreferences } from "../../../hooks/usePreferences";
import { SKELETON_VARIANT } from "../../../drive/components/Skeleton/types";
import Skeleton from "../../../Webchat/components/WebchatLoader/subComponents/Skeleton/Skeleton";
import { validateModuleIsEnabled } from "../../helpers/loggedUser";
import { companySelector } from "../../../app/selectors/company";
import { PERMISSION_LEVELS } from "../../../auth/constants";

const SKELETON = Array.from({ length: 11 });

export function NewSidebar() {
  const { t: translations } = useTranslation(["sidebar"]);

  const companyStore = useSelector(companiesSelector);

  const preferencesCompany = usePreferences();

  const permissions = useSelector(permissionsSelector);

  const location = useLocation();

  const logo = useMemo(
    () =>
      preferencesCompany?.preferences?.picture?.navigationHeader || omuniSVG,
    [preferencesCompany]
  );

  const companyCode = companyStore?.company?.code;

  const hasPreferences = preferencesCompany.preferences?.module_configuration;

  const [selectedRoute, setSelectedRoute] = useState(null);

  const [openSubmenu, setOpenSubmenu] = useState(null);

  const [isHovered, setIsHovered] = useState(false);

  const sidebarRef = useRef(null);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setOpenSubmenu(null);
  };

  const ContainerClassName = `${styles.container} ${
    isHovered ? styles.hovered : styles.notHovered
  }`;

  const handleSelected = (key) => {
    setSelectedRoute(key);
    setIsHovered(false);
    setOpenSubmenu(null);
  };

  const handleSubmenu = (key) => {
    if (openSubmenu === key) {
      // Cuando se cierra, hacer scroll hacia arriba suavemente
      if (sidebarRef.current) {
        sidebarRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
      // Esperar a que termine el scroll antes de cerrar el menú
      setTimeout(() => {
        setOpenSubmenu(null);
      }, 300);
      return;
    }
    setOpenSubmenu(key);
    // Agregar un pequeño delay para asegurar que el submenú se haya renderizado
    setTimeout(() => {
      if (sidebarRef.current) {
        sidebarRef.current.scrollTo({
          top: sidebarRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const findKey = (findKeyParams) => {
    const { route } = findKeyParams;

    const pathname = location.pathname;

    const path = `/${pathname.split("/")[1]}/${pathname.split("/")[2]}`;

    if (!route?.routes) {
      return route.key;
    }

    const findedKey = route.routes.find((child) => {
      if (child?.path?.includes(path)) {
        return true;
      }
    });

    return findedKey?.key;
  };

  const findSubmenuKey = (findSubmenuKeyProps) => {
    const { route } = findSubmenuKeyProps;

    if (!route?.routes) {
      return null;
    }

    return route.key;
  };

  /**
   * Determines if the user has authorization for automation-related actions.
   *
   * @param {Object} permissions - The permissions object.
   * @param {Object} permissions.permissions - The nested permissions structure.
   * @param {boolean} [permissions.permissions.workflowTransitions] - Permission for workflow transitions.
   * @param {boolean} [permissions.permissions.users?.listUser] - Permission to list users.
   * @param {boolean} [permissions.permissions.rules?.getRule] - Permission to get rules.
   * @returns {boolean} - Returns `true` if any of the specified permissions are granted.
   */
  const authorizedAutomations = useMemo(() => {
    return (
      permissions?.permissions?.workflowTransitions?.getWorkflowTransitions ===
        PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.users?.listUser === PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.rules?.getRule === PERMISSION_LEVELS.ALL
    );
  }, [permissions]);

  const authorizedConfigurations = useMemo(() => {
    return (
      permissions?.permissions?.tokens?.readTokens === PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.users?.listUser === PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.customAttributes?.getCustomAttributes ===
        PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.templates?.getTemplates ===
        PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.channels?.listChannels ===
        PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.departments?.getDepartment ===
        PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.profiles?.getProfile ===
        PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.notificationChannels?.getNotificationChannel ===
        PERMISSION_LEVELS.ALL ||
      permissions?.permissions?.webCustomization?.getWebCustomization ===
        PERMISSION_LEVELS.ALL
    );
  }, [permissions]);

  useEffect(() => {
    const pathname = location.pathname;

    const pathSimple = `/${pathname.split("/")[1]}`;

    const pathSubmenu = `/${pathname.split("/")[1]}/${pathname.split("/")[2]}`;

    const route = links.find((link) => {
      const subitem = link?.routes?.find((route) =>
        route.path.includes(pathSubmenu)
      );
      if (link?.routes && subitem) {
        return true;
      }

      if (link?.path?.includes(pathSimple)) {
        return true;
      }
    });

    if (!route) {
      return;
    }

    const key = findKey({ route });

    const submenuKey = findSubmenuKey({ route });

    setSelectedRoute(key);

    setOpenSubmenu(submenuKey);
  }, []);

  const configurationModules =
    preferencesCompany.preferences?.module_configuration || [];

  const links = [
    {
      key: "dashboard",
      label: translations("sidebar:dashboard"),
      path: "/dashboard",
      status: STATUS.ACTIVE,
      icon: ICONS.DASHBOARD,
      hasAuthorized:
        permissions?.permissions?.dashboard?.getDashboard ===
          PERMISSION_LEVELS.ALL &&
        validateModuleIsEnabled(MODULES.DASHBOARD, configurationModules),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      key: "olds-tickets",
      label: translations("sidebar:tickets"),
      path: "/tickets",
      status: STATUS.ACTIVE,
      icon: ICONS.TICKETS,
      hasAuthorized:
        permissions?.permissions?.cases?.listCases > PERMISSION_LEVELS.NONE &&
        validateModuleIsEnabled(MODULES.CASES, configurationModules),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      key: "tickets",
      label: translations("sidebar:tickets"),
      path: `${omuniWeb}/tickets`,
      migration: true,
      status: STATUS.NEW,
      icon: ICONS.TICKETS,
      hasAuthorized:
        permissions?.permissions?.cases?.listCases > PERMISSION_LEVELS.NONE &&
        validateModuleIsEnabled(MODULES.TICKETS, configurationModules),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      key: "contacts",
      label: translations("sidebar:contacts"),
      path: `${omuniWeb}/contacts`,
      status: STATUS.NEW,
      migration: true,
      icon: ICONS.CONTACTS,
      hasAuthorized:
        permissions?.permissions?.contacts?.listContacts ===
          PERMISSION_LEVELS.ALL &&
        validateModuleIsEnabled(MODULES.CONTACTS, configurationModules),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      key: "social",
      label: translations("sidebar:social"),
      path: "/social",
      status: STATUS.ACTIVE,
      icon: ICONS.SOCIAL,
      hasAuthorized:
        permissions?.permissions?.publications?.listPublications ===
          PERMISSION_LEVELS.ALL &&
        validateModuleIsEnabled(MODULES.SOCIALS, configurationModules),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      key: "statistics",
      label: translations("sidebar:statistics"),
      path: "/statistics",
      status: STATUS.ACTIVE,
      icon: ICONS.STATISTICS,
      hasAuthorized:
        permissions?.permissions?.reports?.readReport ===
          PERMISSION_LEVELS.ALL &&
        validateModuleIsEnabled(MODULES.STATISTICS, configurationModules),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      key: "integrations",
      label: translations("sidebar:integrations"),
      path: "/integrations",
      status: STATUS.ACTIVE,
      icon: ICONS.INTEGRATIONS,
      hasAuthorized:
        permissions?.permissions?.integrations?.getIntegrations ===
          PERMISSION_LEVELS.ALL &&
        validateModuleIsEnabled(MODULES.INTEGRATIONS, configurationModules),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      key: "drive",
      label: translations("sidebar:drive"),
      path: "/drive",
      status: [COMPANIES.TEAM_BLUE_TEST, COMPANIES.ECUADOR].includes(
        companyCode
      )
        ? STATUS.ACTIVE
        : STATUS.INACTIVE,
      icon: ICONS.DRIVE,
      hasAuthorized: validateModuleIsEnabled(
        MODULES.FILE_MANAGER,
        configurationModules
      ),
      environment: ENVIRONMENTS.TEST,
    },
    {
      key: "outgoing_campaigns",
      label: translations("sidebar:outgoing_campaigns"),
      path: `${omuniWeb}/outgoing-campaigns`,
      icon: ICONS.CAMPAING,
      status: STATUS.DEMO,
      migration: true,
      hasAuthorized: validateModuleIsEnabled(
        MODULES.CAMPAIGNS,
        configurationModules
      ),
      environment: ENVIRONMENTS.TEST,
    },
    {
      key: "formularios",
      label: translations("sidebar:forms"),
      path: `${omuniWeb}/forms`,
      icon: ICONS.FORMS,
      status: STATUS.BETA,
      migration: true,
      hasAuthorized: permissions?.permissions?.forms?.getForms ===
      PERMISSION_LEVELS.ALL && validateModuleIsEnabled(
        MODULES.FORMS,
        configurationModules
      ),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      key: "calendar",
      label: translations("sidebar:calendar"),
      path: `${omuniWeb}/calendar`,
      icon: ICONS.CALENDAR,
      status: STATUS.BETA,
      migration: true,
      hasAuthorized: validateModuleIsEnabled(
        MODULES.CALENDAR,
        configurationModules
      ),
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      path: `${omuniWeb}/audit`,
      status: STATUS.NEW,
      key: "settings_audit",
      label: translations("sidebar:audit"),
      icon: ICONS.AUDIT,
      migration: true,
      hasAuthorized:
        permissions?.permissions?.audit?.getAudit === PERMISSION_LEVELS.ALL,
      environment: ENVIRONMENTS.PRODUCTION,
    },
    {
      label: translations("sidebar:contactCenter"),
      key: "contactCenter",
      hasAuthorized:
        (permissions?.permissions?.contactCenter?.viewDashboard ===
          PERMISSION_LEVELS.ALL ||
          permissions?.permissions?.contactCenter?.viewRoutingRules ===
            PERMISSION_LEVELS.ALL ||
          permissions?.permissions?.contactCenter?.viewAgents ===
            PERMISSION_LEVELS.ALL ||
          permissions?.permissions?.contactCenter?.viewQueues ===
            PERMISSION_LEVELS.ALL ||
          permissions?.permissions?.contactCenter?.viewBreaks ===
            PERMISSION_LEVELS.ALL ||
          permissions?.permissions?.contactCenter?.viewSkills ===
            PERMISSION_LEVELS.ALL) &&
        validateModuleIsEnabled(MODULES.CONTACT_CENTER, configurationModules),
      icon: ICONS.CONTACT_CENTER,
      routes: [
        {
          key: "contactCenter_dashboards",
          label: translations("sidebar:contactCenterDashboards"),
          path: `${omuniWeb}/contact-center/dashboards`,
          migration: true,
          icon: ICONS.DASHBOARD,
          hasAuthorized:
            permissions?.permissions?.contactCenter?.viewDashboard ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          key: "contactCenter_agents",
          label: translations("sidebar:contactCenterAgents"),
          path: `${omuniWeb}/contact-center/agents`,
          migration: true,
          icon: ICONS.AGENTS,
          hasAuthorized:
            permissions?.permissions?.contactCenter?.viewRoutingRules ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          key: "contactCenter_routing_rules",
          label: translations("sidebar:contactCenterRoutingRules"),
          path: `${omuniWeb}/contact-center/routing-rules`,
          migration: true,
          icon: ICONS.ROUTING_RULES,
          hasAuthorized:
            permissions?.permissions?.contactCenter?.viewRoutingRules ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          key: "contactCenter_skills",
          label: translations("sidebar:contactCenterSkills"),
          path: `${omuniWeb}/contact-center/skills`,
          migration: true,
          icon: ICONS.SKILLS,
          hasAuthorized:
            permissions?.permissions?.contactCenter?.viewSkills ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          key: "contactCenter_queues",
          label: translations("sidebar:contactCenterQueues"),
          path: `${omuniWeb}/contact-center/queues`,
          migration: true,
          icon: ICONS.QUEUES,
          hasAuthorized:
            permissions?.permissions?.contactCenter?.viewQueues ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          key: "contactCenter_breaks",
          label: translations("sidebar:contactCenterBreaks"),
          path: `${omuniWeb}/contact-center/breaks`,
          migration: true,
          icon: ICONS.BREAKS,
          hasAuthorized:
            permissions?.permissions?.contactCenter?.viewBreaks ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
      ],
    },
    {
      label: translations("sidebar:automation"),
      key: "automation",
      icon: ICONS.AUTOMATION,
      hasAuthorized:
        authorizedAutomations &&
        validateModuleIsEnabled(MODULES.AUTOMATION, configurationModules),
      routes: [
        {
          key: "automation_bots",
          label: translations("sidebar:bots"),
          path: `${omuniWeb}/automation/bots`,
          migration: true,
          status: STATUS.NEW,
          icon: ICONS.BOTS,
          hasAuthorized:
            permissions?.permissions?.users?.listUser ===
              PERMISSION_LEVELS.ALL &&
            validateModuleIsEnabled(MODULES.BOTS, configurationModules),
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          key: "automation_rules",
          label: translations("sidebar:rules"),
          path: `${omuniWeb}/automation/rules`,
          status: STATUS.ACTIVE,
          icon: ICONS.RULES,
          migration: true,
          hasAuthorized:
            permissions?.permissions?.rules?.getRule ===
              PERMISSION_LEVELS.ALL &&
            validateModuleIsEnabled(MODULES.RULES, configurationModules),
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          key: "automation_workflows",
          label: translations("sidebar:workflows"),
          path: "/automation/workflows",
          status: STATUS.ACTIVE,
          icon: ICONS.WORKFLOWS,
          hasAuthorized:
            permissions?.permissions?.workflowTransitions
              ?.getWorkflowTransitions === PERMISSION_LEVELS.ALL &&
            validateModuleIsEnabled(MODULES.WORKFLOW, configurationModules),
          environment: ENVIRONMENTS.PRODUCTION,
        },
      ],
    },
    {
      label: translations("sidebar:configuration"),
      icon: ICONS.CONFIGURATION,
      key: "configuration",
      hasAuthorized:
        authorizedConfigurations &&
        validateModuleIsEnabled(MODULES.CONFIGURATION, configurationModules),
      routes: [
        {
          path: `${omuniWeb}/settings/api-keys`,
          status: STATUS.NEW,
          key: "settings_apikeys",
          label: translations("sidebar:apikeys"),
          icon: ICONS.API_KEYS,
          migration: true,
          hasAuthorized:
            permissions?.permissions?.tokens?.readTokens ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          path: `${omuniWeb}/settings/users`,
          status: STATUS.NEW,
          key: "settings_users",
          label: translations("sidebar:users"),
          icon: ICONS.USERS,
          migration: true,
          hasAuthorized:
            permissions?.permissions?.users?.listUser === PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          path: `${omuniWeb}/settings/custom-attributes`,
          status: STATUS.NEW,
          key: "settings_attributes",
          migration: true,
          label: translations("sidebar:attributes"),
          icon: ICONS.ATTRIBUTES,
          hasAuthorized:
            permissions?.permissions?.customAttributes?.getCustomAttributes ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          path: "/settings/channels",
          status: STATUS.ACTIVE,
          key: "settings_channels",
          label: translations("sidebar:channels"),
          icon: ICONS.CHANNELS,
          hasAuthorized:
            permissions?.permissions?.channels?.listChannels ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          path: `${omuniWeb}/settings/departments`,
          status: STATUS.NEW,
          key: "settings_areas",
          label: translations("sidebar:areas"),
          icon: ICONS.AREAS,
          hasAuthorized:
            permissions?.permissions?.departments?.getDepartments ===
            PERMISSION_LEVELS.ALL,
          migration: true,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          path: "/settings/profiles",
          status: STATUS.ACTIVE,
          key: "settings_profile",
          label: translations("sidebar:profile"),
          icon: ICONS.PROFILES,
          hasAuthorized:
            permissions?.permissions?.profiles?.getProfile ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          path: `${omuniWeb}/templates`,
          status: STATUS.NEW,
          key: "settings_templates",
          label: translations("sidebar:templates"),
          icon: ICONS.TEMPLATES,
          hasAuthorized:
            permissions?.permissions?.templates?.getTemplates ===
            PERMISSION_LEVELS.ALL,
          migration: true,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          path: "/settings/notifications",
          status: STATUS.ACTIVE,
          key: "settings_notifications",
          label: translations("sidebar:notifications"),
          icon: ICONS.NOTIFICATIONS,
          hasAuthorized:
            permissions?.permissions?.notificationChannels
              ?.getNotificationChannel === PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
        {
          path: "/settings/webConfiguration",
          status: STATUS.ACTIVE,
          key: "settings_preference",
          label: translations("sidebar:preferences"),
          icon: ICONS.PREFERENCES,
          hasAuthorized:
            permissions?.permissions?.webCustomization?.get ===
            PERMISSION_LEVELS.ALL,
          environment: ENVIRONMENTS.PRODUCTION,
        },
      ],
    },
  ];

  const getItemType = (link) => {
    if (
      environment === ENVIRONMENTS.PRODUCTION &&
      (link.environment === ENVIRONMENTS.TEST ||
        link.environment === ENVIRONMENTS.LOCAL)
    ) {
      return;
    }
    if (link.routes && !link.migration && link.hasAuthorized)
      return (
        <SubMenuItem
          key={link.key}
          id={link.key}
          selected={selectedRoute}
          openSubmenu={openSubmenu}
          handleSelected={handleSelected}
          handleSubmenu={handleSubmenu}
          routes={link.routes}
          location={location}
          collapsed={!isHovered}
          label={link.label}
          path={link.path}
          icon={link.icon}
        />
      );

    if (!link.routes && !link.migration && link.hasAuthorized)
      return (
        <Item
          key={link.key}
          id={link.key}
          status={link.status}
          selected={selectedRoute}
          handleSelected={handleSelected}
          label={link.label}
          path={link.path}
          icon={link.icon}
          location={location}
          collapsed={!isHovered}
        />
      );

    if (link.migration && link.hasAuthorized)
      return (
        <ItemMigration
          key={link.key}
          status={link.status}
          id={link.key}
          selected={selectedRoute}
          handleSelected={handleSelected}
          label={link.label}
          path={link.path}
          icon={link.icon}
          location={location}
          collapsed={!isHovered}
        />
      );
  };

  return (
    <>
      <div
        className={ContainerClassName}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={styles.logoWrapper}
          style={{ "--logo": `url(${logo})` }}
        />
        <div className={styles.separator}></div>
        <div className={styles.SidebarItemsWrappers} ref={sidebarRef}>
          {permissions && hasPreferences
            ? links.map((link) => getItemType(link))
            : SKELETON.map((_, index) => (
                <div className={styles.SkeletonWrapper}>
                  <Skeleton
                    key={index}
                    height="2.5rem"
                    width="2.5rem"
                    borderRadius="0.5rem"
                  />
                </div>
              ))}
        </div>
        <Version collapsed={!isHovered} />
      </div>
      <div
        className={`${styles.backdrop} ${
          isHovered ? styles.hovered : styles.notHovered
        }`}
      ></div>
    </>
  );
}
